/**
 * Permit lifecycle, corrections, fees & eligibility (companion §2.8 detail actions).
 *
 * Surfaces the capability-tier actions the detail screen didn't: guarded lifecycle advance,
 * AHJ corrections, off-platform fee recording (track-only), and warn-only license eligibility.
 *
 * Wiring (mobile inv. 7): useQuery(getPermitApplication, listCorrections) + useMutation
 * (advancePermit, recordFeePayment) + on-demand checkEligibility. Loading/empty/error render.
 * Prepare-to-attest; fees are track-only. NEVER imports @smartbox/*.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPermitApplication,
  listCorrections,
  advancePermit,
  recordFeePayment,
  checkEligibility,
  type PermitApplicationRow,
  type PermitCorrectionRow,
  type EligibilityResult,
} from '../../../src/api/permits';
import { Screen, Text, Card, Button, Badge, Select } from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

const ADVANCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ready', label: 'Ready' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'sufficiency_review', label: 'Sufficiency review' },
  { value: 'corrections_required', label: 'Corrections required' },
  { value: 'in_review', label: 'In review' },
  { value: 'fees_due', label: 'Fees due' },
  { value: 'issued', label: 'Issued' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'closed', label: 'Closed' },
];

function correctionBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'resolved': return { key: 'paid', label: 'Resolved' };
    case 'resubmitted': return { key: 'inProgress', label: 'Resubmitted' };
    default: return { key: 'overdue', label: 'Open' };
  }
}

export default function PermitLifecycleScreen() {
  const { appId } = useLocalSearchParams<{ appId: string }>();
  const queryClient = useQueryClient();

  const [nextStatus, setNextStatus] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const appQuery = useQuery({ queryKey: ['permit-application', appId], queryFn: () => getPermitApplication(appId!), enabled: !!appId, staleTime: 15_000 });
  const correctionsQuery = useQuery({ queryKey: ['permit-corrections', appId], queryFn: () => listCorrections(appId!), enabled: !!appId, staleTime: 15_000 });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['permit-application', appId] });
    void queryClient.invalidateQueries({ queryKey: ['permit-corrections', appId] });
    setActionError(null);
  };
  const onErr = (e: unknown) => setActionError(extractErrorMessage(e, 'Action failed.'));

  const advanceMutation = useMutation({ mutationFn: (s: string) => advancePermit(appId!, s), onSuccess: () => { refresh(); setNextStatus(null); }, onError: onErr });
  const feeMutation = useMutation({ mutationFn: () => recordFeePayment(appId!, paymentMethod ?? undefined), onSuccess: refresh, onError: onErr });
  const eligibilityMutation = useMutation({ mutationFn: () => checkEligibility(undefined, app?.jurisdiction_id ?? undefined), onSuccess: setEligibility, onError: onErr });

  if (!appId) {
    return <Screen><View style={styles.centered} testID="permit-lifecycle-screen"><Text variant="body" color="danger">No permit application ID provided.</Text></View></Screen>;
  }
  if (appQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centered} testID="permit-lifecycle-screen">
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading" />
          <Text variant="body" color="secondary">Loading…</Text>
        </View>
      </Screen>
    );
  }
  if (appQuery.isError) {
    return (
      <Screen>
        <View style={styles.centered} testID="permit-lifecycle-screen">
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{extractErrorMessage(appQuery.error, 'Failed to load application.')}</Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => appQuery.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  const app: PermitApplicationRow | undefined = appQuery.data;
  const feeStatus = (app as unknown as { fee_status?: string })?.fee_status ?? 'none';
  const corrections = correctionsQuery.data ?? [];

  return (
    <Screen scroll>
      <View testID="permit-lifecycle-screen" style={styles.section}>
        <Text variant="title">Lifecycle & corrections</Text>

        {/* Guarded lifecycle advance */}
        <Card>
          <Text variant="bodyStrong">Record a status change</Text>
          <Text variant="small" color="secondary">Records the AHJ's response; the backend enforces legal transitions.</Text>
          <Select options={ADVANCE_OPTIONS} value={nextStatus} onChange={setNextStatus} />
          <Button
            title="Advance status"
            testID="permit-advance"
            size="sm"
            fullWidth={false}
            loading={advanceMutation.isPending}
            disabled={advanceMutation.isPending || !nextStatus}
            onPress={() => nextStatus && advanceMutation.mutate(nextStatus)}
            accessibilityLabel="Record a permit status change"
          />
        </Card>

        {/* Fees — track only */}
        <Card>
          <Text variant="bodyStrong">Fees</Text>
          <Text variant="small" color="secondary">Current: {humanize(feeStatus)}. BEAM tracks fees; it never charges them.</Text>
          <Select
            options={[{ value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'check', label: 'Check' }, { value: 'e_check', label: 'E-check' }, { value: 'contractor_escrow', label: 'Contractor escrow' }]}
            value={paymentMethod}
            onChange={setPaymentMethod}
          />
          <Button
            title="Record fee paid (off-platform)"
            testID="permit-record-fee"
            size="sm"
            fullWidth={false}
            loading={feeMutation.isPending}
            disabled={feeMutation.isPending}
            onPress={() => feeMutation.mutate()}
            accessibilityLabel="Record that the permit fee was paid off-platform"
          />
        </Card>

        {/* Warn-only license eligibility */}
        <Card>
          <Text variant="bodyStrong">License eligibility</Text>
          <Button
            title="Check eligibility"
            testID="permit-check-eligibility"
            variant="secondary"
            size="sm"
            fullWidth={false}
            loading={eligibilityMutation.isPending}
            onPress={() => eligibilityMutation.mutate()}
            accessibilityLabel="Check license eligibility for this jurisdiction"
          />
          {eligibility ? (
            <View style={styles.section} testID="permit-eligibility-result">
              <Text variant="small" color={eligibility.ok ? 'secondary' : 'danger'}>
                {eligibility.ok ? 'A valid license authorizes this filing.' : 'Warnings:'}
              </Text>
              {eligibility.warnings.map((w, i) => (
                <Text key={i} variant="small" color="danger">• {w.message}</Text>
              ))}
            </View>
          ) : null}
        </Card>

        {/* Corrections */}
        <View style={styles.section}>
          <Text variant="label" color="secondary">AHJ corrections</Text>
          {correctionsQuery.isLoading ? (
            <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading corrections" />
          ) : corrections.length === 0 ? (
            <Card><Text variant="body" color="secondary">No corrections requested.</Text></Card>
          ) : (
            <View style={styles.section} testID="permit-corrections-list">
              {corrections.map((c: PermitCorrectionRow) => {
                const b = correctionBadge(c.status);
                return (
                  <Card key={c.id}>
                    <View style={styles.header}>
                      <Text variant="bodyStrong" style={styles.flex1}>{c.summary ?? 'Correction'}</Text>
                      <Badge status={b.key} label={b.label} />
                    </View>
                    <Text variant="small" color="secondary">Source: {humanize(c.source)}</Text>
                  </Card>
                );
              })}
            </View>
          )}
        </View>

        {actionError ? (
          <Card><Text variant="bodyStrong" color="danger" accessibilityRole="alert">{actionError}</Text></Card>
        ) : null}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md, paddingHorizontal: theme.spacing['2xl'] },
  section: { gap: theme.spacing.sm },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
  flex1: { flex: 1 },
});
